import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaCloudUploadAlt,
  FaTicketAlt,
} from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES, adminPath } from '../constants/adminPortal';
import '../styles/AdminCreateSupportTicket.css';
import { graphqlRequest } from '../utils/graphqlRequest';
import { CREATE_SUPPORT_TICKET_MUTATION } from '../graphql/supportTicket';

const CATEGORIES = [
  { value: '', label: 'Select category' },
  { value: 'technical', label: 'Technical issue' },
  { value: 'billing', label: 'Billing & payments' },
  { value: 'account', label: 'Account access' },
  { value: 'feature', label: 'Feature request' },
  { value: 'other', label: 'Other' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', previewClass: 'cst-badge--low' },
  { value: 'normal', label: 'Normal', previewClass: 'cst-badge--normal' },
  { value: 'high', label: 'High', previewClass: 'cst-badge--high' },
  { value: 'urgent', label: 'Urgent', previewClass: 'cst-badge--urgent' },
];

const MIN_DESCRIPTION = 20;
const MAX_FILE_MB = 10;
const PRIORITY_TO_GQL = {
  low: 'LOW',
  normal: 'NORMAL',
  high: 'HIGH',
  urgent: 'URGENT',
};

function CreateSupportTicket() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  // Optional prefill, e.g. from Billing & Subscription "Request" buttons.
  const prefill = useLocation().state || {};
  const [subject, setSubject] = useState(prefill.subject || '');
  const [category, setCategory] = useState(prefill.category || '');
  const [priority, setPriority] = useState('normal');
  const [description, setDescription] = useState(prefill.description || '');
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const descLen = description.trim().length;
  const descOk = descLen >= MIN_DESCRIPTION;

  const priorityLabel = useMemo(
    () => PRIORITIES.find((p) => p.value === priority)?.label || 'Normal',
    [priority],
  );

  const priorityPreviewClass = useMemo(
    () => PRIORITIES.find((p) => p.value === priority)?.previewClass || 'cst-badge--normal',
    [priority],
  );

  const createdPreview = useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
    [],
  );

  const addFiles = useCallback((fileList) => {
    const next = Array.from(fileList || []);
    const valid = next.filter((f) => f.size <= MAX_FILE_MB * 1024 * 1024);
    if (valid.length < next.length) {
      setError(`Some files exceed ${MAX_FILE_MB}MB and were skipped.`);
    } else {
      setError('');
    }
    setFiles((prev) => [...prev, ...valid]);
  }, []);

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    addFiles(e.dataTransfer.files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!subject.trim()) {
      setError('Please enter a subject.');
      return;
    }
    if (!category) {
      setError('Please select a category.');
      return;
    }
    if (!descOk) {
      setError(`Description must be at least ${MIN_DESCRIPTION} characters.`);
      return;
    }

    setSubmitting(true);
    try {
      const gqlPriority = PRIORITY_TO_GQL[priority];
      const data = await graphqlRequest(CREATE_SUPPORT_TICKET_MUTATION, {
        input: {
          subject: subject.trim(),
          category,
          priority: gqlPriority,
          description: description.trim(),
          attachmentFileNames: files.map((f) => f.name),
        },
      });

      const t = data?.createSupportTicket;
      const ticketNo = t?.ticketNumber != null ? String(t.ticketNumber).padStart(3, '0') : null;
      navigate(adminPath(ADMIN_ROUTES.HELP_SUPPORT), {
        state: {
          supportBanner: ticketNo
            ? `Your support ticket (TKT-${ticketNo}) was created. You will receive a confirmation email shortly.`
            : 'Your support ticket was created. You will receive a confirmation email shortly.',
          createdSupportTicket: t || null,
        },
      });
    } catch {
      setError('Could not create ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => navigate(-1);

  return (
    <div className="cst-page">
      <header className="cst-hero">
        <div className="cst-hero-inner">
          <button type="button" className="cst-back" onClick={goBack} aria-label="Go back">
            <FaArrowLeft />
          </button>
          <div className="cst-hero-icon" aria-hidden>
            <FaTicketAlt />
          </div>
          <div className="cst-hero-text">
            <h1>Create Support Ticket</h1>
            <p>Submit a support request and our team will assist you as soon as possible.</p>
          </div>
        </div>
      </header>

      <div className="cst-layout">
        <form className="cst-form-card" onSubmit={handleSubmit} noValidate>
          {error ? <div className="cst-banner-error">{error}</div> : null}

          <h2 className="cst-form-title">Ticket Information</h2>

          <label className="cst-label" htmlFor="cst-subject">
            Subject <span className="cst-req">*</span>
          </label>
          <input
            id="cst-subject"
            type="text"
            className="cst-input"
            placeholder="Brief summary of your issue"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            autoComplete="off"
          />

          <div className="cst-row-2">
            <div>
              <label className="cst-label" htmlFor="cst-category">
                Category <span className="cst-req">*</span>
              </label>
              <select
                id="cst-category"
                className="cst-input"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {CATEGORIES.map((opt) => (
                  <option key={opt.value || 'empty'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="cst-label" htmlFor="cst-priority">
                Priority
              </label>
              <select
                id="cst-priority"
                className="cst-input"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                {PRIORITIES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="cst-label" htmlFor="cst-desc">
            Description <span className="cst-req">*</span>
          </label>
          <textarea
            id="cst-desc"
            className="cst-textarea"
            rows={8}
            placeholder="Please provide a detailed description of your issue or request..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <p className={`cst-hint ${descOk ? 'ok' : ''}`}>
            Minimum {MIN_DESCRIPTION} characters ({descLen}/{MIN_DESCRIPTION}+)
          </p>

          <div className="cst-label">Attachments (optional)</div>
          <div
            className={`cst-dropzone ${dragActive ? 'active' : ''}`}
            onDrop={onDrop}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            <FaCloudUploadAlt className="cst-upload-icon" aria-hidden />
            <strong>Upload supporting files</strong>
            <span>Screenshots, documents, or other relevant files</span>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            className="cst-file-input"
            multiple
            accept="image/*,.pdf,.doc,.docx,.txt"
            onChange={(e) => addFiles(e.target.files)}
          />
          <button
            type="button"
            className="cst-choose-files portal-btn-primary"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            Choose Files
          </button>

          {files.length > 0 ? (
            <ul className="cst-file-list">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`}>
                  <span>{f.name}</span>
                  <button type="button" onClick={() => removeFile(i)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="cst-actions">
            <button type="button" className="cst-cancel portal-btn-secondary" onClick={goBack}>
              Cancel
            </button>
            <button type="submit" className="cst-submit portal-btn-primary" disabled={submitting}>
              <FaTicketAlt aria-hidden />
              {submitting ? 'Creating…' : 'Create Ticket'}
            </button>
          </div>
        </form>

        <aside className="cst-preview-card">
          <h2 className="cst-preview-title">Ticket Preview</h2>

          <div className="cst-preview-field">
            <span className="cst-preview-label">Ticket ID</span>
            <div className="cst-preview-value cst-preview-value--mono">Auto-generated</div>
          </div>

          <div className="cst-preview-field cst-preview-inline">
            <span className="cst-preview-label">Status</span>
            <span className="cst-badge cst-badge--status">
              <span className="cst-badge-dot" />
              Open
            </span>
          </div>

          <div className="cst-preview-field cst-preview-inline">
            <span className="cst-preview-label">Priority</span>
            <span className={`cst-badge ${priorityPreviewClass}`}>{priorityLabel}</span>
          </div>

          <div className="cst-preview-field">
            <span className="cst-preview-label">Created</span>
            <div className="cst-preview-value">{createdPreview}</div>
          </div>

          <div className="cst-preview-field cst-preview-inline">
            <span className="cst-preview-label">Expected response</span>
            <span className="cst-badge cst-badge--response">Within 24 hours</span>
          </div>

          {subject.trim() ? (
            <div className="cst-preview-field">
              <span className="cst-preview-label">Subject</span>
              <p className="cst-preview-subject">{subject.trim()}</p>
            </div>
          ) : null}

          <div className="cst-preview-info">
            <FaCheckCircle className="cst-preview-info-icon" aria-hidden />
            <p>
              After submission you&apos;ll receive a confirmation email. Track progress anytime under{' '}
              <strong>Help &amp; Support</strong> → Your Tickets.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default CreateSupportTicket;
